// Credits HUD rendered in PixiJS screen-space.
//
// Single Text element anchored to the top-right of the viewport.
// Phase 4 Step 14: display-only; driven by explicit update() calls
// from main.ts (joined handler seeds; Step 19 wires creditsUpdated
// for the push path). No animation — polish lands post-Step-14.
//
// Like inventory-ui.ts, the class is a dumb renderer: it holds no
// subscription logic. Main.ts message handlers drive the updates.

import { Application, Text } from "pixi.js";

// 8px edge margin matches inventory-ui's TOOLBAR_PAD_BOTTOM so the
// HUD layer reads with one consistent gutter.
const EDGE_MARGIN_PX = 8; // contracts:allow-literal — HUD edge margin
const CREDITS_SYMBOL = "\u20B5"; // ₵ — African currency glyph chosen as a neutral sci-fi credit marker

export class CreditsHUD {
  private readonly text: Text;

  constructor(private readonly app: Application) {
    this.text = new Text({
      text: this.formatLabel(0),
      style: {
        fontSize: 16, // contracts:allow-literal — HUD readout font size in px
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 }, // contracts:allow-literal — outline stroke width
        fontFamily: "monospace",
        fontWeight: "bold",
      },
    });
    this.text.zIndex = 100; // contracts:allow-literal — above world rendering
    app.stage.sortableChildren = true;
    app.stage.addChild(this.text);

    this.layout();
    app.renderer.on("resize", () => this.layout());
  }

  update(credits: number): void {
    this.text.text = this.formatLabel(credits);
    this.layout();
  }

  private formatLabel(credits: number): string {
    return `${CREDITS_SYMBOL} ${credits}`;
  }

  // Anchors the text's top-right corner at (screenW - margin, margin).
  // PixiJS Text width depends on the current string, so layout() must
  // run after every update() — cheap because it's just two
  // assignments on a single node.
  private layout(): void {
    const screenW = this.app.renderer.width;
    this.text.x = screenW - this.text.width - EDGE_MARGIN_PX;
    this.text.y = EDGE_MARGIN_PX;
  }
}
